import { useMemo, useContext, useEffect } from 'react'
import { Card, Stack, SimpleGrid, Spacer } from '@backyard-ui/core'
import { useHits, useInstantSearch, usePagination } from 'react-instantsearch'

import {
  setInsightsInfo,
  type InsightInfoHit
} from 'scripts/react-components/entry-key/utils/analytics'

import { NewProductThumb } from 'shared/components/NewProductThumb'
import { Pagination } from 'shared/components/Pagination'
import { useUserResources } from 'shared/hooks/useUserResources'

import { WishlistContext } from 'site/Wishlist/context'

import {
  onProductClickDataLayer,
  onProductImpressionDataLayer
} from './Content.tracker'
import { useRating } from './queries/use-rating'
import { Loading } from './loading'
import {
  getItemCategory,
  normalize,
  normalizeAssistedSale
} from './Content.utils'
import { useServerContext } from '../../routes/routes.context'
import { IS_CATEGORY } from '../../utils/constants'
import { IS_ON_MOBILE_APP } from '../../constants'

type WishlistContextType = {
  isOnWishlist: (id: string) => boolean
}
interface ContentProps {
  region: string
}

const Content = ({ region }: ContentProps) => {
  const { isAlgoliaAssistedSale } = useServerContext()
  const { userResources } = useUserResources()
  const { nbPages, currentRefinement, refine } = usePagination()
  const { status, indexUiState } = useInstantSearch()
  const { isOnWishlist } = useContext(WishlistContext) as WishlistContextType
  const { items, results, sendEvent } = useHits({
    escapeHTML: false
  })
  const storeSlug = userResources?.storeSlug
  const assistedSaleStoreSlug = isAlgoliaAssistedSale ? storeSlug : null

  const products = useMemo(
    () =>
      items.map(item => ({
        ...item,
        ...(assistedSaleStoreSlug
          ? normalizeAssistedSale(item, assistedSaleStoreSlug)
          : normalize(item, region, IS_CATEGORY))
      })),
    [items, region, assistedSaleStoreSlug]
  )

  useEffect(() => {
    if (products.length) {
      onProductImpressionDataLayer({
        region,
        products: products,
        term: indexUiState.query ?? '',
        page: results?.page ? results?.page + 1 : 1
      })
    }
  }, [products, indexUiState.query, region, results?.page])

  useEffect(() => {
    if (currentRefinement > 0) {
      window.scrollTo({ top: 0, behavior: 'instant' })
    }
  }, [currentRefinement])

  const { data: rating = [] } = useRating({
    ids: products.map(p => Number(p.id))
  })

  const setPage = (page: number) => {
    refine(page - 1)
  }

  const isRefetching = status === 'loading' || status === 'stalled'

  if (isRefetching && items.length === 0) {
    return <Loading />
  }

  const onProductClick = (product: InsightInfoHit, index: number) => {
    if (assistedSaleStoreSlug) {
      const hit = product as Record<string, unknown>
      const storeData = hit[`store_${assistedSaleStoreSlug}`] as
        | { promotionalPrice?: number }
        | undefined
      setInsightsInfo(product, region, undefined, storeData?.promotionalPrice)
    } else {
      setInsightsInfo(product, region)
    }
    sendEvent('click', product, 'productListClick')
    onProductClickDataLayer({
      index,
      region,
      product,
      page: currentRefinement + 1,
      term: indexUiState.query ?? '',
      inWishlist: isOnWishlist(product.id)
    })
  }

  return (
    <Stack align="center" direction="column">
      <Card.Root>
        <Card.Body>
          <SimpleGrid
            columns={{
              initial: 2,
              sm: 3,
              md: 4
            }}>
            {products.map((product, index) => (
              <NewProductThumb
                partnerName="algolia"
                key={product.id}
                onClick={() => onProductClick(product, index)}
                product={product}
                reviewRating={rating.find(
                  item => item.productLm === Number(product.id)
                )}
                fullWidth
                index={index}
                itemList={`entry-key-${indexUiState.query}`}
                itemCategory={
                  product?.categoryTree?.length
                    ? getItemCategory(product.categoryTree)
                    : ''
                }
              />
            ))}
          </SimpleGrid>
        </Card.Body>
      </Card.Root>

      {nbPages > 1 && (
        <Pagination
          currentPage={currentRefinement + 1}
          totalPages={nbPages}
          setPage={setPage}
          pageCount={nbPages}
          page={currentRefinement + 1}
        />
      )}

      {IS_ON_MOBILE_APP && (
        <Spacer
          axis="vertical"
          size="10"
          data-testid="mobile-context-bottom-spacer"
        />
      )}
    </Stack>
  )
}

export default Content
